import { getCameraById, getCameraOfProjectById } from '@api/services/camera.service';
import { getCctvCameraById } from '@api/services/cctv-camera.service';
import { getProjectHierarchy } from '@api/services/project.service';
import useDataQuery from '@hooks/use-data-query';
import { Breadcrumb, IBreadcrumbItem } from '@hyperflake/react-ui-library';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface AppendItemsProps {
    label: string;
    to: string;
}
interface ProjectHierarchyBreadcrumbProps {
    to?: string;
    appendItems?: AppendItemsProps[];
}

const ProjectHierarchyBreadcrumb = (props: ProjectHierarchyBreadcrumbProps) => {
    const { to, appendItems } = props;

    const { clientId, projectId, cameraId, cctvCameraId } = useParams();
    const navigate = useNavigate();

    const projectHierarchyQuery = useDataQuery(
        [clientId, 'projectHierarchy', projectId],
        () => getProjectHierarchy(clientId, projectId),
        {
            enabled: Boolean(projectId),
        }
    );
    const cameraForBreadcrumbQuery = useDataQuery(['cameraForBreadcrumb', cameraId], () => getCameraById(cameraId), {
        enabled: Boolean(cameraId),
    });
    const cctvCameraForBreadcrumbQuery = useDataQuery(
        ['cctvCameraForBreadcrumb', cctvCameraId],
        () => getCctvCameraById(cctvCameraId),
        {
            enabled: Boolean(cctvCameraId),
        }
    );

    const items = useMemo(() => {
        if (projectHierarchyQuery.isLoading || projectHierarchyQuery.isError) return [...appendItems];

        const projectHeirarchyList = projectHierarchyQuery.data.map((p, i) => {
            const href = `/clients/${clientId}/projects/${p._id}/project-details`;

            // Check condition on hasCamera
            // const href = p.parentId
            // ? `/clients/${clientId}/projects/${p._id}/project-details`
            // : `/clients/${clientId}/projects/${p._id}`;

            return { label: p.name, to: href };
        });

        const cameraBreadcrumbItem = {
            label: cameraForBreadcrumbQuery?.data?.name,
            to: `/clients/${clientId}/projects/${projectId}/cameras/${cameraId}`,
        };
        const cctvCameraBreadcrumbItem = {
            label: cctvCameraForBreadcrumbQuery?.data?.name,
            to: `/clients/${clientId}/projects/${projectId}/cctv-cameras/${cctvCameraId}`,
        };

        return cameraForBreadcrumbQuery.data
            ? [...appendItems, ...projectHeirarchyList, cameraBreadcrumbItem]
            : cctvCameraForBreadcrumbQuery.data
            ? [...appendItems, ...projectHeirarchyList, cctvCameraBreadcrumbItem]
            : [...appendItems, ...projectHeirarchyList];
    }, [
        projectHierarchyQuery.isLoading,
        projectHierarchyQuery.data,
        cameraForBreadcrumbQuery.data,
        cameraForBreadcrumbQuery.isLoading,
        cctvCameraForBreadcrumbQuery.data,
        cctvCameraForBreadcrumbQuery.isLoading,
        appendItems,
        to,
    ]);

    const transformedItems: IBreadcrumbItem[] = items.map((item) => ({
        label: item.label,
        onClick: () => navigate(item.to),
    }));

    return <Breadcrumb items={transformedItems} />;
};

ProjectHierarchyBreadcrumb.defaultProps = {
    appendItems: [],
    to: '',
};

export default ProjectHierarchyBreadcrumb;
